import {Box, Typography} from "@mui/material";
import React, {useCallback, useContext, useEffect, useLayoutEffect, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import {useSearchParams} from "react-router-dom";
import {
    editMessage as editMessageApi,
    fetchMessages,
    getChatPlatforms,
    postMessageWithMedia,
    requestCloseChat,
    requestOpenChat
} from "../http/chatAPI";
import {fetchOneChatCategory} from "../http/categoryAPI";
import ChatList from "../components/Chats/ChatList";
import ChatBlock from "../components/Chats/ChatBlock";
import {Context} from "../index";
import {useSocket} from "../hooks/useSocket";
import {useMessages} from "../hooks/useMessages";
import {useChats} from "../hooks/useChats";
import {getUserById} from "../http/userAPI";
import PatientCard from "../components/Chats/PatientCard";
import {usePatientRequests} from "../hooks/usePatientRequests";
import ChatCategoryGuard from "../components/ChatCategoryGuard";

const ChatPage = observer(() => {
    const socket = useSocket();
    const {messages, addMessage, prependMessages, resetMessages, updateMessage} = useMessages();
    const [searchParams] = useSearchParams();
    const categoryId = searchParams.get('category');
    const [currentCategory, setCurrentCategory] = useState(null);

    const {
        loadChats,
        updateLastMessage,
        updateLastMessageByUserId,
        loading,
        resetLoading,
        selectedChat,
        selectChat,
        handleChatTaken,
        handleChatClosed,
        openedChats,
        closedChats,
        myChats,
        search,
        setSearch,
        sortBy,
        setSortBy,
        filterByUnread,
        setFilterByUnread,
        setChangingChat,
        changingChat,
        hasMore,
        markAsRead,
        handleChatUpdated,
    } = useChats(categoryId);

    const {
        loadAllRequests,
        loadingRequests,

        activeAppointments,
        activePapers,
        activeCertificates,

        appointmentRequests,
        certificateRequests,
        paperRequests,

        markAppointmentRequest,
        addAppointmentRequest,
        updateAppointmentRequest,

        markPaperRequest,
        addPaperRequest,
        updatePaperRequest,

        addCertificateRequest,
        updateCertificateRequest,
        markCertificateRequest,
    } = usePatientRequests();

    const {user, chatStats} = useContext(Context);
    const closedChatsStats = chatStats.getClosedStats(categoryId || null);
    const openedChatsStats = chatStats.getOpenedStats(categoryId || null);
    const chatRef = useRef(null);
    const [offset, setOffset] = useState(0);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [slipToBottom, setSlipToBottom] = useState(false);
    const [fixScrollPosition, setFixScrollPosition] = useState(false);
    const [prevScrollHeight, setPrevScrollHeight] = useState(null);
    const [prevScrollTop, setPrevScrollTop] = useState(null);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [currentMessage, setCurrentMessage] = useState('');
    const [availablePlatforms, setAvailablePlatforms] = useState([]);
    const [selectedPlatform, setSelectedPlatform] = useState(null);
    const [platformManuallySet, setPlatformManuallySet] = useState(false);

    const handleInsertTemplate = (templateText) => {
        setCurrentMessage(templateText);
    };

    const loadMessages = useCallback(async () => {
        if (loadingMessages || !hasMoreMessages || !selectedChat) return;
        setLoadingMessages(true);
        const limit = 20;
        setPrevScrollHeight(chatRef.current?.getScrollHeight());
        setPrevScrollTop(chatRef.current?.getScrollTop());

        const data = await fetchMessages(selectedChat.id, limit, offset, 'DESC');

        if (data.length < limit) setHasMoreMessages(false);
        prependMessages(data);
        setOffset(prev => prev + data.length);
        setLoadingMessages(false);
    }, [selectedChat, loadingMessages, hasMoreMessages, offset, prependMessages]);

    useLayoutEffect(() => {
        if (!loadingMessages && chatRef.current) {
            if (slipToBottom) {
                chatRef.current?.scrollToBottom();
            } else if (fixScrollPosition) {
                chatRef.current.revertScrollHeight(prevScrollHeight, prevScrollTop);
                setFixScrollPosition(false);
            }
        }
    }, [slipToBottom, messages, loadingMessages]);

    const loadMore = () => {
        setFixScrollPosition(true);
        loadMessages();
    }

    const onScrolledUp = () => {
        setSlipToBottom(false)
        setPrevScrollHeight(chatRef.current?.getScrollHeight());
        setPrevScrollTop(chatRef.current?.getScrollTop());
    }

    const onScrolledDown = () => {
        setSlipToBottom(true);
    }

    useEffect(() => {
        // Avoid loading messages while chat is being changed to prevent fetching for the previous chat
        if (changingChat) return;
        if (selectedChat && offset === 0 && hasMoreMessages) {
            loadMessages();
        }
    }, [selectedChat, changingChat, offset, hasMoreMessages, loadMessages]);

    // Подписка на сокеты
    useEffect(() => {
        const socketInstance = socket.current;
        if (!socketInstance) return;

        // CHATS
        const onNewMessage = async (data) => {
            await updateLastMessage(data.chatId);

            if (selectedChat?.id === data.chatId) {
                addMessage(data);
                if (data.fromPatient && data.platform && !platformManuallySet) {
                    const hasThatPlatform = availablePlatforms.some(p => p.platform === data.platform);
                    if (hasThatPlatform) {
                        setSelectedPlatform(data.platform);
                    }
                }
            }

            setPrevScrollHeight(chatRef.current?.getScrollHeight());
            setPrevScrollTop(chatRef.current?.getScrollTop());
        };

        const onEditMessageSocket = async (data) => {
            if (selectedChat?.id === data.chatId) {
                updateMessage(data);
            }
        };

        const onChatOpened = ({chatId}) => {
            handleChatTaken(chatId);
        };

        const onChatClosed = ({chatId}) => {
            handleChatClosed(chatId);
        };

        const onChatUpdated = (data) => {
            handleChatUpdated(data);
        };

        socketInstance.on('chat:newMessage', onNewMessage);
        socketInstance.on('chat:editMessage', onEditMessageSocket);
        socketInstance.on('chat:opened', onChatOpened);
        socketInstance.on('chat:closed', onChatClosed);
        socketInstance.on('chat:updated', onChatUpdated);

        // REQUESTS

        const requestHandler = (data, hander) => {
            if (!data?.id) {
                return;
            }

            if (data?.userId === selectedChat?.userId) {
                hander(data);
            }

            updateLastMessageByUserId(data.userId);
        }

        const onAppointmentCreated = (data) => requestHandler(data, addAppointmentRequest);
        const onAppointmentUpdated = (data) => requestHandler(data, updateAppointmentRequest);
        const onPaperCreated = (data) => requestHandler(data, addPaperRequest);
        const onPaperUpdated = (data) => requestHandler(data, updatePaperRequest);
        const onCertificateCreated = (data) => requestHandler(data, addCertificateRequest);
        const onCertificateUpdated = (data) => requestHandler(data, updateCertificateRequest);

        socketInstance.on('requestAppointment:created', onAppointmentCreated);
        socketInstance.on('requestAppointment:updated', onAppointmentUpdated);
        socketInstance.on('requestPaper:created', onPaperCreated);
        socketInstance.on('requestPaper:updated', onPaperUpdated);
        socketInstance.on('requestCertificate:created', onCertificateCreated);
        socketInstance.on('requestCertificate:updated', onCertificateUpdated);

        return () => {
            socketInstance.off('chat:newMessage', onNewMessage);
            socketInstance.off('chat:editMessage', onEditMessageSocket);
            socketInstance.off('chat:opened', onChatOpened);
            socketInstance.off('chat:closed', onChatClosed);
            socketInstance.off('chat:updated', onChatUpdated);

            socketInstance.off('requestAppointment:updated', onAppointmentUpdated);
            socketInstance.off('requestAppointment:created', onAppointmentCreated);
            socketInstance.off('requestPaper:updated', onPaperUpdated);
            socketInstance.off('requestPaper:created', onPaperCreated);
            socketInstance.off('requestCertificate:updated', onCertificateUpdated);
            socketInstance.off('requestCertificate:created', onCertificateCreated);
        };
    }, [socket,
        selectedChat,
        addMessage,
        updateLastMessage,
        updateMessage,
        handleChatTaken,
        handleChatClosed,
        handleChatUpdated,
        addAppointmentRequest,
        updateAppointmentRequest,
        addPaperRequest,
        updatePaperRequest,
        addCertificateRequest,
        updateCertificateRequest,
        updateLastMessageByUserId,
        availablePlatforms,
        platformManuallySet]);

    useEffect(() => {
        if (!user.user?.id) return;
        chatStats.loadClosedStats(user.user.id, categoryId || null).catch((err) => {
            console.error("Ошибка загрузки статистики чатов по категории:", err);
        });
        chatStats.loadOpenedStats(user.user.id, categoryId || null).catch((err) => {
            console.error("Ошибка загрузки статистики открытых чатов по категории:", err);
        });
        chatStats.loadMyStats(user.user.id, categoryId || null).catch((err) => {
            console.error("Ошибка загрузки статистики моих чатов по категории:", err);
        });
    }, [user.user?.id, categoryId, chatStats]);

    const onChatChanged = async (chatId) => {
        if (selectedChat?.id === chatId) return;

        setChangingChat(true);
        await selectChat(chatId);
        setSelectedPatient(null);
        resetMessages();
        setOffset(0);
        setHasMoreMessages(true);
        setSlipToBottom(true);
        setCurrentMessage('');
        setAvailablePlatforms([]);
        setSelectedPlatform(null);
        setPlatformManuallySet(false);
        setChangingChat(false);
    };

    // Загружаем платформы пациента при открытии чата
    useEffect(() => {
        if (!selectedChat?.id) return;
        let cancelled = false;
        getChatPlatforms(selectedChat.id).then((data) => {
            if (cancelled) return;
            setAvailablePlatforms(data?.platforms || []);
            setSelectedPlatform(data?.defaultPlatform || null);
            setPlatformManuallySet(false);
        }).catch((err) => {
            console.error('Не удалось получить платформы пациента:', err);
            setAvailablePlatforms([]);
            setSelectedPlatform(null);
        });
        return () => { cancelled = true; };
    }, [selectedChat?.id]);

    const sendMessage = (message, files = []) => {
        if (!selectedChat) return;
        const text = (message || '').trim();
        const hasFiles = Array.isArray(files) && files.length > 0;
        if (!text && !hasFiles) return;

        setSendingMessage(true);
        const promise = postMessageWithMedia(selectedChat.id, user.user.id, text, files, selectedPlatform)

        promise.catch(err => {
            alert(err?.response?.data?.message || err.message);
        }).finally(() => {
            setSendingMessage(false);
        });
    };

    const openChat = async (chat) => {
        await requestOpenChat(chat.id, user.user.id);
        await onChatChanged(chat.id);
    };

    const closeChat = async (chat) => {
        await requestCloseChat(chat.id, user.user.id);
        await onChatChanged(chat.id);
    };

    const editMessage = async (messageId, newText) => {
        if (!selectedChat) return;
        const trimmed = (newText || '').trim();
        if (!trimmed) return;
        try {
            await editMessageApi(selectedChat.id, messageId, user.user.id, trimmed);
        } catch (e) {
            alert(e?.response?.data?.message || e.message || 'Ошибка редактирования сообщения');
        }
    }

    useEffect(() => {
        if (!selectedChat?.userId) return;
        getUserById(selectedChat.userId).then((data) => {
            setSelectedPatient(data.user);
        }).catch(err => alert('Ошибка получения подробных данных пациента'));
    }, [selectedChat]);

    useEffect(() => {
        if (categoryId) {
            fetchOneChatCategory(categoryId).then((data) => {
                setCurrentCategory(data);
            }).catch(err => console.error('Ошибка загрузки категории:', err));
        } else {
            setCurrentCategory(null);
        }
        
        // Сбрасываем состояние и перезагружаем чаты при смене категории
        resetMessages();
        setSelectedPatient(null);
        setOffset(0);
        setHasMoreMessages(true);
        setSlipToBottom(true);
        setCurrentMessage('');
    }, [categoryId]);

    return (
        <ChatCategoryGuard>
            <Box sx={{display: "flex", flexDirection: "column", width: "100%", height: "100%", m: 0, overflow: "hidden"}}>
                {currentCategory && (
                    <Box sx={{padding: '0 16px 8px 16px', flexShrink: 0}}>
                        <Typography variant="h6">{currentCategory.name}</Typography>
                    </Box>
                )}
                <Box sx={{display: "flex", width: "100%", flex: 1, m: 0, alignItems: "flex-start", overflow: "hidden"}}>
                    <ChatList loading={loading}
                          resetLoading={resetLoading}
                          selectedChat={selectedChat}
                          loadChats={loadChats}
                          selectChat={onChatChanged}
                          myChats={myChats}
                          closedChats={closedChats}
                          closedChatsStats={closedChatsStats}
                          openedChatsStats={openedChatsStats}
                          openedChats={openedChats}
                          setSearch={setSearch}
                          search={search}
                          sortBy={sortBy}
                          setSortBy={setSortBy}
                          filterByUnread={filterByUnread}
                          setFilterByUnread={setFilterByUnread}
                          hasMore={hasMore}
                          categoryId={categoryId}
                          onMarkAsRead={markAsRead}
                />
                {selectedChat &&
                    <>
                        <ChatBlock messages={messages}
                                   chat={selectedChat}
                                   changingChat={changingChat}
                                   sendMessage={sendMessage}
                                   openChatHandler={openChat}
                                   closeChatHandler={closeChat}
                                   onEditMessage={editMessage}
                                   onSlipOff={onScrolledUp}
                                   onSlipOn={onScrolledDown}
                                   onLoadMore={loadMore}
                                   isLoading={loadingMessages}
                                   sendingMessage={sendingMessage}
                                   messageValue={currentMessage}
                                   onMessageChange={setCurrentMessage}
                                   onInsertTemplate={handleInsertTemplate}
                                   availablePlatforms={availablePlatforms}
                                   selectedPlatform={selectedPlatform}
                                   onPlatformChange={(p) => {
                                       setSelectedPlatform(p);
                                       setPlatformManuallySet(true);
                                   }}
                                   ref={chatRef}
                        />
                        <PatientCard
                            loadAllRequests={loadAllRequests}
                            loadingRequests={loadingRequests}
                            appointmentRequests={appointmentRequests}
                            certificateRequests={certificateRequests}
                            paperRequests={paperRequests}

                            activeAppointments={activeAppointments}
                            activePapers={activePapers}
                            activeCertificates={activeCertificates}

                            markAppointmentRequest={markAppointmentRequest}
                            markCertificateRequest={markCertificateRequest}
                            markPaperRequest={markPaperRequest}

                            selectedPatient={selectedPatient}
                            changingChat={changingChat}
                        />
                    </>
                }
                </Box>
            </Box>
        </ChatCategoryGuard>
    );
});

export default ChatPage;
